import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Container,
	Heading,
	Image,
	Link,
	SimpleGrid,
	Stack,
	Text,
} from "@chakra-ui/react";
import { Link as ReactLink } from "react-router-dom";

import { MarketingLayout } from "../components/marketing-layout";
import { InlineContactSection } from "../components/marketing-layout/InlineContactSection";
import { NewsList } from "../components/newsList";
import { useCmsApi } from "../services/useCmsApi";

interface IResearchArea {
	title: string;
	description: string;
	link: string;
	buttonText: string;
}

const defaultResearchAreas: IResearchArea[] = [
	{
		title: "Weed Prediction",
		description:
			"Prediction combines historical patterns with current-season crop and weed conditions to help identify where patches are likely to emerge next.",
		link: "/weed-prediction",
		buttonText: "View Weed Prediction",
	},
	{
		title: "Herbicide Resistance Detection",
		description:
			"Geco also supports targeted resistance scouting and field-level decision-making where resistant weeds are most likely to cause trouble.",
		link: "/herbicide-resistance",
		buttonText: "View Resistance Detection",
	},
];

export const ResearchPage = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [researchAreas, setResearchAreas] = useState<IResearchArea[]>(defaultResearchAreas);

	useEffect(() => {
		cmsApi.getSiteSetting("researchAreas").then((s) => {
			if (s?.value) {
				try {
					const parsed = JSON.parse(s.value) as IResearchArea[];
					if (Array.isArray(parsed) && parsed.length > 0) setResearchAreas(parsed);
				} catch { /* fallback to defaults */ }
			}
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
	<MarketingLayout>
		<>
			<Box bg="green.800" color="white" py={{ base: "16", md: "24" }}>
				<Container maxW="1480px">
					<Stack spacing="4" maxW="3xl">
						<Heading size={{ base: "2xl", md: "3xl" }} fontWeight="extrabold">
							Research
						</Heading>
						<Text fontSize={{ base: "lg", md: "xl" }} opacity={0.92}>
							Geco combines agronomic understanding with multi-year satellite analysis to build practical,
							field-ready weed intelligence for growers.
						</Text>
						<Text opacity={0.88}>
							Explore the core work behind Geco’s predictive weed mapping and herbicide resistance detection.
						</Text>
					</Stack>
					<Image
						src="research-coverage-map.png"
						alt="Geco coverage map"
						maxW="720px"
						w="full"
						mt="8"
						borderRadius="xl"
						bg="white"
						p="3"
					/>
				</Container>
			</Box>

			<Box py={{ base: "16", md: "20" }}>
				<Container maxW="1480px">
					<Stack spacing="10">
						<Stack spacing="3" maxW="3xl">
							<Heading size="lg" color="gray.800">
								Current Research Areas
							</Heading>
							<Text color="gray.600">
								Geco’s current public story is focused on two practical outputs: predictive weed mapping
								and targeted herbicide resistance detection.
							</Text>
						</Stack>
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing="6">
							{researchAreas.map((area) => (
								<Stack
									key={area.title}
									p="6"
									bg="white"
									borderRadius="xl"
									boxShadow="sm"
									border="1px solid"
									borderColor="gray.100"
									spacing="4"
								>
									<Heading size="md" color="gray.800">
										{area.title}
									</Heading>
									<Text color="gray.600" flex="1">
										{area.description}
									</Text>
									<Link as={ReactLink} to={area.link}>
										<Button colorScheme="green" variant="outline">
											{area.buttonText}
										</Button>
									</Link>
								</Stack>
							))}
						</SimpleGrid>
					</Stack>
				</Container>
			</Box>

			<Box bg="gray.50" py={{ base: "12", md: "16" }}>
				<Container maxW="1480px">
					<Stack spacing="4" maxW="3xl">
						<Heading size="lg" color="gray.800">
							Media and Updates
						</Heading>
						<Text color="gray.600">
							Industry coverage, interviews, and updates can help provide extra context on how the work is evolving.
						</Text>
					</Stack>
					<NewsList />
				</Container>
			</Box>

			<InlineContactSection />
		</>
	</MarketingLayout>
	);
};
